import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Copy } from "lucide-react";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { copyToClipboard } from "../../../../utils/copyToClipboard";

function CredentialValue({ label, value, copyLabel, copiedMessage }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">{label}</p>
      <div className="flex items-center gap-2">
        {/* break-all y no truncate: si el portapapeles falla, el comercio tiene que
            poder seleccionar el valor completo a mano. */}
        <code className="min-w-0 flex-1 break-all rounded bg-bg-subtle px-2 py-1.5 font-mono text-xs text-fg">
          {value}
        </code>
        <Button
          variant="outline"
          size="sm"
          aria-label={copyLabel}
          onClick={() => copyToClipboard(value, copiedMessage, t)}
          iconLeft={<Copy className="h-3.5 w-3.5" aria-hidden="true" />}
        >
          {t("Copiar")}
        </Button>
      </div>
    </div>
  );
}

/**
 * Muestra el `clientSecret` recién creado o rotado. Es la ÚNICA vez que se ve.
 *
 * No se puede cerrar (Esc, fondo ni botón) hasta que el comercio confirma que lo
 * guardó. Perderlo no es grave, porque se puede rotar, pero rotar deja sin servicio
 * al sistema que ya lo estaba usando.
 *
 * El secreto vive solo en la prop `secret`: al cerrar, el padre la pone en null y
 * desaparece del DOM. No se guarda en storage ni en la consola.
 */
export default function SecretRevealModal({ secret, onClose }) {
  const { t } = useTranslation();
  const checkboxId = useId();

  // Se guarda QUÉ secreto confirmó el comercio y no un booleano, así un secreto
  // nuevo (otra credencial o una rotación) arranca sin confirmar por construcción.
  // Resetear un booleano con un efecto deja una carrera: el efecto pendiente puede
  // correr después del clic en la casilla y desmarcarla.
  const [confirmedSecret, setConfirmedSecret] = useState(null);
  const saved = Boolean(secret) && confirmedSecret === secret;

  const handleClose = () => {
    if (saved) onClose();
  };

  return (
    <Modal
      open={Boolean(secret)}
      onClose={handleClose}
      hideClose={!saved}
      size="md"
      title={secret?.reason === "rotated" ? t("Secreto rotado") : t("Credencial creada")}
      footer={
        <Button variant="primary" onClick={handleClose} disabled={!saved}>
          {t("Listo")}
        </Button>
      }
    >
      {secret && (
        <div className="space-y-4">
          <div
            className="flex items-start gap-2 rounded-lg border border-border bg-bg-muted px-4 py-3"
            role="note"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-warning" aria-hidden="true" />
            <p className="text-sm text-fg-muted">
              {t("Copia el client secret ahora: no se volverá a mostrar. Si lo pierdes, tendrás que rotarlo.")}
            </p>
          </div>

          <CredentialValue
            label={t("Client ID")}
            value={secret.clientId}
            copyLabel={t("Copiar Client ID")}
            copiedMessage={t("Client ID copiado")}
          />
          <CredentialValue
            label={t("Client secret")}
            value={secret.clientSecret}
            copyLabel={t("Copiar client secret")}
            copiedMessage={t("Client secret copiado")}
          />

          <div className="flex items-start gap-2">
            <input
              id={checkboxId}
              type="checkbox"
              className="mt-0.5 h-4 w-4"
              checked={saved}
              onChange={(event) => setConfirmedSecret(event.target.checked ? secret : null)}
            />
            <label htmlFor={checkboxId} className="text-sm text-fg">
              {t("Ya guardé el client secret en un lugar seguro")}
            </label>
          </div>
        </div>
      )}
    </Modal>
  );
}
